import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { getMyWork, listMyFacilityTasks, requestTicket } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useTaskPriorities as usePriorities, taskPriorityCode } from "../lib/priorities";
import { getCurrentUser } from "../lib/auth";
import { formatDate } from "../lib/format";
import { FACILITY_PRIORITIES, FACILITY_STATUSES } from "../lib/constants";
import type { MyWorkChangeRow, MyWorkOrderRow, MyWorkResponse, TaskRow, TicketRow } from "../lib/types";
import { ChangeRiskBadge, ChangeStatusBadge } from "./ChangeManagementPage";
import { PageBar } from "../components/Layout";
import { Icon, type IconName } from "../components/Icon";
import { Modal } from "../components/Modal";
import { PriorityBadge, StatusBadge } from "../components/Badge";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";

const FACILITY_ST_LABEL: Record<string, string> = Object.fromEntries(
  FACILITY_STATUSES.map((s) => [s.code, s.label]),
);
const FACILITY_PRI_LABEL: Record<string, string> = Object.fromEntries(
  FACILITY_PRIORITIES.map((p) => [p.code, p.label]),
);

const TIMER_SORT: Record<string, SortAccessor<MyWorkResponse["open_timers"][number]>> = {
  task: (t) => t.task_title || t.task_id,
  started: (t) => t.started_at,
};
const MYWORK_TASK_SORT: Record<string, SortAccessor<TaskRow>> = {
  title: (t) => t.title,
  status: (t) => t.status_code ?? t.status,
  priority: (t) => t.priority_code,
};
const MYWORK_TICKET_SORT: Record<string, SortAccessor<TicketRow>> = {
  subject: (t) => t.subject,
  status: (t) => t.status_code,
  priority: (t) => t.priority_code,
};
const MYWORK_FACILITY_SORT: Record<string, SortAccessor<MyWorkOrderRow>> = {
  number: (r) => r.number,
  title: (r) => r.title,
  status: (r) => FACILITY_ST_LABEL[r.status] ?? r.status,
  priority: (r) => FACILITY_PRI_LABEL[r.priority] ?? r.priority,
};
const MYWORK_TEAM_SORT: Record<string, SortAccessor<TicketRow>> = {
  subject: (t) => t.subject,
  assignee: (t) => t.assignee_name,
  status: (t) => t.status_code,
  priority: (t) => t.priority_code,
};
const MYWORK_CHANGE_SORT: Record<string, SortAccessor<MyWorkChangeRow>> = {
  title: (r) => r.title,
  status: (r) => r.status,
  risk: (r) => r.risk,
};

type TabKey = "tasks" | "tickets" | "action_items" | "requested" | "work_orders" | "facility_requests" | "changes";

export function MyWorkPage() {
  const user = getCurrentUser();

  // Identity is resolved from the session server-side — no user id is sent.
  const { data, loading, error, reload } = useApi((signal) => getMyWork(signal), []);
  const priorities = usePriorities();
  const [requesting, setRequesting] = useState(false);
  const [tab, setTab] = useState<TabKey>("tickets");

  const tasks = data?.tasks ?? [];
  const actionItems = data?.action_items ?? [];
  const tickets = data?.tickets ?? [];
  const requested = data?.requested_tickets ?? [];
  const workOrders = data?.work_orders ?? [];
  const facilityRequests = data?.facility_requests ?? [];
  const changes = data?.changes ?? [];
  const team = data?.team_tickets ?? [];
  const timers = data?.open_timers ?? [];
  const { rows: sortedTimers, sort: timerSort, toggle: timerToggle } = useTableSort(timers, TIMER_SORT);
  const { rows: sortedTeam, sort: teamSort, toggle: teamToggle } = useTableSort(team, MYWORK_TEAM_SORT);

  // One catalog drives both the colored stat tiles (selectors) and the workload
  // distribution bar — each work type gets a stable icon + accent color.
  const CATS: { key: TabKey; label: string; icon: IconName; color: string; count: number }[] = [
    { key: "tickets", label: "Assigned Tickets", icon: "tickets", color: "#16a34a", count: tickets.length },
    { key: "requested", label: "Requested Tickets", icon: "bell", color: "#f59e0b", count: requested.length },
    { key: "tasks", label: "Project Tasks", icon: "projects", color: "#2563eb", count: tasks.length },
    { key: "action_items", label: "Action Items", icon: "action-items", color: "#7c3aed", count: actionItems.length },
    { key: "changes", label: "Change Management", icon: "target", color: "#4f46e5", count: changes.length },
    { key: "work_orders", label: "Work Order Assigned", icon: "wrench", color: "#dc2626", count: workOrders.length },
    { key: "facility_requests", label: "Work Order Requested", icon: "equipment", color: "#0891b2", count: facilityRequests.length },
  ];
  const totalOpen = CATS.reduce((n, c) => n + c.count, 0);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || user?.email || "there").split(/\s+/)[0];

  return (
    <>
      <PageBar crumbs="My Work" />
      <div className="content">
        <div className="mywork-hero">
          <div>
            <h1 style={{ margin: 0 }}>
              {greeting}, {firstName} <span aria-hidden="true">👋</span>
            </h1>
            <div className="sub">
              {totalOpen > 0
                ? `You have ${totalOpen} open item${totalOpen === 1 ? "" : "s"} across your queues.`
                : "You're all caught up — nothing on your plate right now. 🎉"}
            </div>
          </div>
          <button className="btn small" onClick={reload} disabled={loading}>
            Refresh
          </button>
        </div>

        {loading ? (
          <Loading label="Loading your work…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <>
            {/* Workload at a glance: total + a colored distribution bar. */}
            <div className="card mywork-summary">
              <div className="card-body">
                <div className="mywork-summary-row">
                  <div className="mywork-total">
                    <span className="mywork-total-num">{totalOpen}</span>
                    <span className="mywork-total-label">open item{totalOpen === 1 ? "" : "s"}</span>
                  </div>
                  <div className="mywork-bar-wrap">
                    <div className="mywork-bar" role="img" aria-label="Work distributed across your queues">
                      {totalOpen > 0 ? (
                        CATS.filter((c) => c.count > 0).map((c) => (
                          <span
                            key={c.key}
                            className="mywork-bar-seg"
                            style={{ width: `${(c.count / totalOpen) * 100}%`, background: c.color }}
                            title={`${c.label}: ${c.count}`}
                          />
                        ))
                      ) : (
                        <span className="mywork-bar-seg" style={{ width: "100%", background: "var(--border)" }} />
                      )}
                    </div>
                    <div className="mywork-legend">
                      {CATS.filter((c) => c.count > 0).map((c) => (
                        <span key={c.key} className="mywork-legend-item">
                          <span className="dot" style={{ background: c.color }} />
                          {c.label} <strong>{c.count}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <MyFacilityTasksCard />

            {timers.length > 0 ? (
              <div className="card" style={{ marginBottom: 18 }}>
                <div className="card-head">
                  <h3>Running timers</h3>
                </div>
                <div className="card-body tight">
                  <table className="data">
                    <thead>
                      <tr>
                        <SortTh field="task" sort={timerSort} onSort={timerToggle}>Project task</SortTh>
                        <SortTh field="started" sort={timerSort} onSort={timerToggle}>Started</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTimers.map((t) => (
                        <tr key={t.id}>
                          <td>{t.task_title || <span className="mono">{t.task_id.slice(0, 8)}</span>}</td>
                          <td>{formatDate(t.started_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="mywork-tiles" role="tablist" aria-label="My Work lists">
              {CATS.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={tab === c.key}
                  className={`mywork-tile${tab === c.key ? " active" : ""}`}
                  style={{ ["--tile" as string]: c.color }}
                  onClick={() => setTab(c.key)}
                >
                  <span className="mywork-tile-icon" aria-hidden="true">
                    <Icon name={c.icon} />
                  </span>
                  <span className="mywork-tile-text">
                    <span className="mywork-tile-count">{c.count}</span>
                    <span className="mywork-tile-label">{c.label}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="card">
              {tab === "requested" ? (
                <div className="card-head">
                  <h3>Requested tickets</h3>
                  <button className="btn small primary" onClick={() => setRequesting(true)}>
                    Request a ticket
                  </button>
                </div>
              ) : null}
              <div className="card-body tight">
                {tab === "tasks" ? (
                  <TaskTable
                    rows={tasks}
                    priorities={priorities}
                    empty="No project tasks assigned to you."
                    linkFor={(t) => `/projects/${t.project_id}`}
                  />
                ) : tab === "action_items" ? (
                  <TaskTable
                    rows={actionItems}
                    priorities={priorities}
                    empty="No action items assigned to you."
                    linkFor={(t) => `/action-items?open=${t.id}`}
                  />
                ) : tab === "tickets" ? (
                  <TicketTable rows={tickets} empty="No tickets assigned to you." />
                ) : tab === "requested" ? (
                  <TicketTable
                    rows={requested}
                    empty="You haven't requested any tickets yet. Click “Request a ticket” to open one — the helpdesk will pick it up."
                  />
                ) : tab === "work_orders" ? (
                  <FacilityTable rows={workOrders} empty="No work orders assigned to you." />
                ) : tab === "changes" ? (
                  <ChangeTable rows={changes} empty="No change requests you own, requested, or need to approve." />
                ) : (
                  <FacilityTable rows={facilityRequests} empty="You haven't submitted any facility requests." />
                )}
              </div>
            </div>

            {/* Manager view: open tickets assigned to anyone in this user's
                reporting subtree. Hidden entirely for users with no reports. */}
            {team.length > 0 ? (
              <div className="card" style={{ marginTop: 18 }}>
                <div className="card-head">
                  <h3>My team's tickets</h3>
                  <span className="faint">{team.length}</span>
                </div>
                <div className="card-body tight">
                  <table className="data">
                    <thead>
                      <tr>
                        <SortTh field="subject" sort={teamSort} onSort={teamToggle}>Subject</SortTh>
                        <SortTh field="assignee" sort={teamSort} onSort={teamToggle}>Assignee</SortTh>
                        <SortTh field="status" sort={teamSort} onSort={teamToggle}>Status</SortTh>
                        <SortTh field="priority" sort={teamSort} onSort={teamToggle}>Priority</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTeam.map((t) => (
                        <tr key={t.id}>
                          <td>
                            <Link to={`/tickets/${t.id}`}>{t.subject}</Link>
                          </td>
                          <td>{t.assignee_name || "—"}</td>
                          <td>
                            <StatusBadge code={t.status_code} />
                          </td>
                          <td>{t.priority_code ? <PriorityBadge code={t.priority_code} /> : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      {requesting ? (
        <RequestTicketModal
          onClose={() => setRequesting(false)}
          onCreated={() => {
            setRequesting(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Tasks / Action Items list — both are TaskRow with a status_code|status. */
function TaskTable({
  rows,
  priorities,
  empty,
  linkFor,
}: {
  rows: TaskRow[];
  priorities: ReturnType<typeof usePriorities>;
  empty: string;
  linkFor: (t: TaskRow) => string;
}) {
  const { rows: sorted, sort, toggle } = useTableSort(rows, MYWORK_TASK_SORT);
  if (rows.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <table className="data">
      <thead>
        <tr>
          <SortTh field="title" sort={sort} onSort={toggle}>Title</SortTh>
          <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
          <SortTh field="priority" sort={sort} onSort={toggle}>Priority</SortTh>
        </tr>
      </thead>
      <tbody>
        {sorted.map((t) => {
          const prCode = taskPriorityCode(t, priorities);
          return (
            <tr key={t.id}>
              <td>
                <Link to={linkFor(t)}>{t.title}</Link>
                {t.can_edit === false ? (
                  <span className="pill" title="Shared with you — read-only" style={{ marginLeft: 6 }}>
                    Read-only
                  </span>
                ) : null}
              </td>
              <td>{(t.status_code ?? t.status) ? <StatusBadge code={t.status_code ?? t.status} /> : "—"}</td>
              <td>{prCode ? <PriorityBadge code={prCode} /> : "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TicketTable({ rows, empty }: { rows: TicketRow[]; empty: string }) {
  const { rows: sorted, sort, toggle } = useTableSort(rows, MYWORK_TICKET_SORT);
  if (rows.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <table className="data">
      <thead>
        <tr>
          <SortTh field="subject" sort={sort} onSort={toggle}>Subject</SortTh>
          <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
          <SortTh field="priority" sort={sort} onSort={toggle}>Priority</SortTh>
        </tr>
      </thead>
      <tbody>
        {sorted.map((t) => (
          <tr key={t.id}>
            <td>
              <Link to={`/tickets/${t.id}`}>{t.subject}</Link>
            </td>
            <td>{t.status_code ? <StatusBadge code={t.status_code} /> : "—"}</td>
            <td>{t.priority_code ? <PriorityBadge code={t.priority_code} /> : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Facility work orders / requests — facility status/priority badges. */
function FacilityTable({ rows, empty }: { rows: MyWorkOrderRow[]; empty: string }) {
  const { rows: sorted, sort, toggle } = useTableSort(rows, MYWORK_FACILITY_SORT);
  if (rows.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <table className="data">
      <thead>
        <tr>
          <SortTh field="title" sort={sort} onSort={toggle}>Title</SortTh>
          <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
          <SortTh field="priority" sort={sort} onSort={toggle}>Priority</SortTh>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.id}>
            <td>
              <Link to={`/facilities/${r.id}`}>{r.title}</Link>
            </td>
            <td>
              <span className={`badge facility-st facility-st-${r.status}`} title="Status">
                {FACILITY_ST_LABEL[r.status] ?? r.status}
              </span>
            </td>
            <td>
              <span className={`badge facility-pri facility-pri-${r.priority}`} title="Priority">
                {FACILITY_PRI_LABEL[r.priority] ?? r.priority}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Change requests — change status + risk badges, links to the CM detail. */
function ChangeTable({ rows, empty }: { rows: MyWorkChangeRow[]; empty: string }) {
  const { rows: sorted, sort, toggle } = useTableSort(rows, MYWORK_CHANGE_SORT);
  if (rows.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <table className="data">
      <thead>
        <tr>
          <SortTh field="title" sort={sort} onSort={toggle}>Title</SortTh>
          <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
          <SortTh field="risk" sort={sort} onSort={toggle}>Risk</SortTh>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.id}>
            <td>
              <Link to={`/change-management/${r.id}`}>
                <span className="mono faint">#{r.number}</span> {r.title}
              </Link>
            </td>
            <td><ChangeStatusBadge status={r.status} /></td>
            <td><ChangeRiskBadge risk={r.risk} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** "My facility tasks" (0086) — open to-do tasks assigned to me across work
 *  orders, ordered by due date. A "Today" toggle narrows to due-today-or-overdue;
 *  overdue/today rows are emphasized. Each row links to the parent work order. */
function MyFacilityTasksCard() {
  const [todayOnly, setTodayOnly] = useState(false);
  const { data, loading, error, reload } = useApi(
    (signal) => listMyFacilityTasks({ dueToday: todayOnly }, signal),
    [todayOnly],
  );
  const tasks = data?.tasks ?? [];

  // End of today (local) in unix-seconds: a task is "due" (overdue or today) when
  // its due_date is at/before this instant.
  const endOfToday = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  })();

  if (!loading && !error && tasks.length === 0 && !todayOnly) return null;

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-head">
        <h3>My facility tasks</h3>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <label className="toggle" style={{ fontSize: "0.85em" }}>
            <input
              type="checkbox"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
            />
            <span>Today &amp; overdue</span>
          </label>
          <span className="faint">{tasks.length}</span>
        </div>
      </div>
      <div className="card-body tight">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : tasks.length === 0 ? (
          <EmptyState>
            {todayOnly ? "Nothing due today. 🎉" : "No open facility tasks assigned to you."}
          </EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Task</th>
                <th>Work order</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => {
                const due = t.due_date ?? null;
                const isDue = due != null && due <= endOfToday;
                return (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td>
                      <Link to={`/facilities/${t.request_id}`}>
                        {t.wo_number != null ? `#${t.wo_number}` : "WO"}
                        {t.wo_title ? ` · ${t.wo_title}` : ""}
                      </Link>
                    </td>
                    <td>
                      {due != null ? (
                        <span className={isDue ? "due-emphasis" : undefined} style={isDue ? { color: "#dc2626", fontWeight: 600 } : undefined}>
                          {formatDate(due)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Self-service request modal: subject + details. Creates a ticket as the
 *  current user in the intake ("Pre Ticket") lane for the helpdesk to triage. */
function RequestTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const { run, pending, error } = useMutation(requestTicket);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    const res = await run({ subject: subject.trim(), ...(description.trim() ? { description: description.trim() } : {}) });
    if (res) onCreated();
  }

  return (
    <Modal
      title="Request a ticket"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="request-ticket-form" disabled={pending || !subject.trim()}>
            {pending ? <Spinner label="Submitting…" /> : "Submit request"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <p className="hint" style={{ marginTop: 0 }}>
        Your request goes to the helpdesk as a “Pre Ticket”. They'll assign it and move it to Open.
      </p>
      <form id="request-ticket-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What do you need help with?"
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span className="label">Details</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add any context, steps, or specifics…"
            rows={5}
          />
        </label>
      </form>
    </Modal>
  );
}
